import { useState } from 'react';
import { Archive } from 'lucide-react';
import type { DocumentPublicationListItem } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';

const COLUMN_COUNT = 7;

function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

function recipientLabel(pub: DocumentPublicationListItem): string {
  if (pub.kind === 'individual') return pub.employeeName ?? '—';
  return pub.audience ? t(`documents.templates.audiences.${pub.audience}`) : '—';
}

interface PublicationListProps {
  publications: DocumentPublicationListItem[];
  loading: boolean;
  onRowClick: (publication: DocumentPublicationListItem) => void;
  onArchive: (publication: DocumentPublicationListItem) => Promise<void>;
}

/**
 * Publications table (Task 6, spec §8.4): number/title/kind/recipient,
 * deadline (red once past due), a small acceptance progress bar, publish
 * date and a one-way archive action (publications are immutable historical
 * records — there is no "unarchive", unlike templates).
 */
export function PublicationList({ publications, loading, onRowClick, onArchive }: PublicationListProps) {
  const [archiveTarget, setArchiveTarget] = useState<DocumentPublicationListItem | null>(null);
  const [archiving, setArchiving] = useState(false);

  const handleConfirmArchive = async () => {
    if (!archiveTarget) return;
    try {
      setArchiving(true);
      await onArchive(archiveTarget);
      setArchiveTarget(null);
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-card rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-muted-foreground w-28">{t('documents.publications.number')}</TableHead>
                <TableHead className="text-muted-foreground">{t('documents.templates.name')}</TableHead>
                <TableHead className="text-muted-foreground w-28">{t('documents.publications.kind')}</TableHead>
                <TableHead className="text-muted-foreground w-44">{t('documents.publications.recipient')}</TableHead>
                <TableHead className="text-muted-foreground w-28">{t('documents.publications.deadline')}</TableHead>
                <TableHead className="text-muted-foreground w-36">{t('documents.publications.progress')}</TableHead>
                <TableHead className="text-muted-foreground w-32">{t('documents.publications.publishedAt')}</TableHead>
                <TableHead className="text-muted-foreground w-14" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 4 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT + 1}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : publications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT + 1} className="h-32 text-center">
                    <p className="text-muted-foreground">{t('documents.publications.empty')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                publications.map((pub) => {
                  const overdue = !!pub.deadline && pub.deadline < todayYmd() && pub.acceptanceDone < pub.acceptanceTotal;
                  const progressPct = pub.acceptanceTotal > 0 ? Math.round((pub.acceptanceDone / pub.acceptanceTotal) * 100) : 0;
                  return (
                    <TableRow key={pub.id} className="cursor-pointer" onClick={() => onRowClick(pub)}>
                      <TableCell className="py-2 font-mono text-xs">{pub.number}</TableCell>
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{pub.title}</span>
                          {pub.acceptanceReturned > 0 && (
                            <Badge
                              variant="outline"
                              className="border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                            >
                              {t('documents.publications.returnedBadge', { count: pub.acceptanceReturned })}
                            </Badge>
                          )}
                          {pub.archivedAt && (
                            <Badge variant="outline" className="text-muted-foreground">
                              {t('documents.publications.archived')}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <Badge variant="outline">{t(`documents.publications.kinds.${pub.kind}`)}</Badge>
                      </TableCell>
                      <TableCell className="py-2">{recipientLabel(pub)}</TableCell>
                      <TableCell className={cn('py-2 text-sm', overdue && 'font-medium text-rose-600 dark:text-rose-400')}>
                        {pub.deadline ?? '—'}
                      </TableCell>
                      <TableCell className="py-2">
                        {pub.acceptanceTotal > 0 ? (
                          <div className="flex items-center gap-2">
                            <div className="bg-muted h-1.5 w-16 overflow-hidden rounded-full">
                              <div
                                className="bg-primary h-full rounded-full"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <span className="text-muted-foreground text-xs tabular-nums">
                              {pub.acceptanceDone}/{pub.acceptanceTotal}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground py-2 text-sm">
                        {new Date(pub.publishedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="py-2">
                        {!pub.archivedAt && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              setArchiveTarget(pub);
                            }}
                          >
                            <Archive className="size-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog open={archiveTarget !== null} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documents.publications.archive')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('documents.publications.archiveConfirm', { number: archiveTarget?.number ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={archiving} onClick={handleConfirmArchive}>
              {t('documents.publications.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
